import React, { useEffect, useState, useContext} from 'react';
import { Beforeunload, useBeforeunload } from 'react-beforeunload';
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import actions from '@state/actions';
import selectors from '@state/selectors';
import { IElement, IEntityInformation, IProps, ITextAttributes } from './types';
import * as S from './styles';
import Provider from './Context';
import AppBar from './Editor/AppBar';
import ContentStructure from './Editor/ContentStructure';
import { IAppState } from '@state/types';
import Keyboard from './Editor/Keyboard';
import FontsLoading from './Editor/FontsLoading';
import { updateRenderingTextSegments, getTextElementUnasignedWidth } from 'utils/textMultiStyling'
import { IParsedTree, IUpdateFieldInfo} from 'state/parsedTrees/types';
import { cloneDeep } from 'lodash';
import { IDSConfig, IBrandConfigFont } from '@state/dsconfig/types';
import { checkLoadedFont, getFontsToLoad } from '@utils/fonts';
import { getUserPermission } from '@utils/design';
import { IDesign, PermissionEnum } from '@state/designs/types';
import { ITemplate } from '@state/templates/types';
import LoadingPage from '@components/LoadingPage';
import { loadOEMFont } from '@utils/fonts';
import { brandToDSConfigBrand, fetchBrandById } from '@state/brands/utils';

const Editor:React.FC<IProps> = () => {
  const authContext = useContext(AuthContext);
  const dsConfig: IDSConfig = useSelector(selectors.dsConfig.getDSConfig);
  let { type, id } = useParams<any>();
  const dispatch = useDispatch();
  const [parsedTreeId, setParsedTreeId] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingFonts, setLoadingFonts] = useState<IBrandConfigFont[]>();
  const parsedTree = useSelector((state: IAppState) => selectors.parsedTrees.getParsedTreeById(state, parsedTreeId)); 
  const template: ITemplate | undefined = useSelector(selectors.templates.getTemplate); 
  const design: IDesign | undefined = useSelector(selectors.designs.getDesign);
  const designAuth = useSelector(selectors.designs.getDesignAuth);
  const userFiles = useSelector(selectors.userFiles.getUserFiles);
  const [parsedTreeForRendering, setParsedTreeForRendering] = useState<IParsedTree>();
  
  const userPermission = getUserPermission(design?.createdBy ?? template?.createdBy, authContext.userData?.profile, designAuth, authContext.loggedToken);

  const getUserConfig = () => {
    return new Promise((resolve, reject) => {
      dispatch(actions.userConfig.getUserConfig(`/${authContext?.userData.profile.email}`,(result, error) => {
        if (error) reject(error);
        else resolve(result);
      }))
    });
  }

  const getDesign = (id:string) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.designs.getDesign(`/${id}`,(result, error) => {
        if (error) reject(error);
        else resolve(result);
      }))
    });
  }

  const getDesignAuth = (id: string) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.designs.getDesignAuth(id, (result, error) => {
        if (error) reject(error);
        else resolve(result);
      }))
    });
  }

  const getTemplate = (id:string) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.templates.getTemplate(`/${id}`,(result, error) => {
        if (error) reject(error);
        else resolve(result);
      }))
    });
  }

  const getParsedTree = (id:string) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.parsedTrees.getParsedTree(`/${id}`,(result, error) => {
        if (error) reject(error);
        else resolve(result);
      }))
    });
  }

  const getUserFiles = (email: string) => {
    let query=`?filter=c.user.email=%27${email}%27&orderby=c.id DESC&start=0&pagelength=1`;
    return new Promise((resolve, reject) => {
      dispatch(actions.userFiles.getUserFiles(query,(result, error) => {
        if (error) reject(error);
        else resolve(result);
      }))
    });
  }

  const updateDSConfig = async () => {
    const oemId = template?.tags.oem ?? design?.tags.oem;
    if (oemId) {
      const brandDefinition = dsConfig.brands.find(b => b.id === oemId)
      if (!brandDefinition) {
        const brand = await fetchBrandById(dispatch, oemId)
        if (brand) dispatch(actions.dsConfig.addBrand(brandToDSConfigBrand(brand)))
      }
    }
  }

  const hasAllRenderingInformationAndAsignedWidth = (data: IElement[]): boolean => {
    if (data && data.length>0) {
      for (let i=0;i<data.length;i++) {
        if (data[i].children && data[i].children!.length>0) {
          if (!hasAllRenderingInformationAndAsignedWidth(data[i].children!)) {
            return false
          }
        }
        if (data[i].type === "Text") {
          if ((data[i].attributes as ITextAttributes).partsForRendering === undefined) {
            return false;
          } 
          if ((data[i].attributes as ITextAttributes).width === undefined) {
            return false;
          } 
        }
      }
    }
    return true;
  }

  const includeRenderingInformationAndWidth = (data: IElement[]): IElement[] => {
    if (data && data.length>0) {
      for (let i=0;i<data.length;i++) {
        if (data[i].children && data[i].children!.length>0) {
          data[i].children = includeRenderingInformationAndWidth(data[i].children!);
        }
        if (data[i].type === "Text") {
          if ((data[i].attributes as ITextAttributes).width === undefined) {
            const marginTextPoints = 30
            const width = getTextElementUnasignedWidth(data[i].attributes as ITextAttributes);
            (data[i].attributes as ITextAttributes).width = width + marginTextPoints;
            if((data[i].attributes as ITextAttributes).textAlign === "right") {
              (data[i].attributes as ITextAttributes).x = (data[i].attributes as ITextAttributes).x-marginTextPoints;
            }
            if((data[i].attributes as ITextAttributes).textAlign === "center") {
              (data[i].attributes as ITextAttributes).x = (data[i].attributes as ITextAttributes).x-(marginTextPoints/2);
            }
            data[i].attributes = updateRenderingTextSegments(data[i].attributes);
          } 
          else if ((data[i].attributes as ITextAttributes).partsForRendering === undefined) {
            data[i].attributes = updateRenderingTextSegments(data[i].attributes);
          } 
        }
      }
    }
    return data;
  }

  const updateParsedTreeFieldInfo = ( updateFieldInfo: IUpdateFieldInfo) => {
    return new Promise((resolve, reject) => {
        dispatch(actions.parsedTrees.updateParsedTreeField(updateFieldInfo,(result, error) => {
            if (error) reject(error)
            else resolve(result);
        }))
    });
  }  

  const updateParsedTreeData = (idParsedTree: string, updatedData: IElement[]) => {
    updateParsedTreeFieldInfo({ id: idParsedTree, field: "data", value: updatedData});
  }

  const handleLoadingFonts = async () => {
    
    if (dsConfig && parsedTree && parsedTree.data && parsedTree.data.length > 0) {
      await updateDSConfig();
      const fontsToLoad = getFontsToLoad(dsConfig, parsedTree.data[0]);
      setLoadingFonts(fontsToLoad);
      setLoading(false);
    }
  }

  const finishLoading = () => {
    let updatedParsedTree = cloneDeep(parsedTree)
    if (updatedParsedTree && parsedTree?.data?.length) {
      const hasAllInfoForRendering = hasAllRenderingInformationAndAsignedWidth(parsedTree.data);
      if (!hasAllInfoForRendering) {
        const updatedData: IElement[] = includeRenderingInformationAndWidth(parsedTree.data);
        updatedParsedTree.data = updatedData;
        updateParsedTreeData(parsedTree.id, updatedData);
      }
      setParsedTreeForRendering(updatedParsedTree);
    }
  }

  useEffect(() => {
    const loadingFontsAction = async () => {
      if (loadingFonts && !loading) {
        let newLoadingFonts = cloneDeep(loadingFonts);
        await Promise.all(loadingFonts.map(async (font, index) => {
          if (checkLoadedFont(font) || await loadOEMFont(font.oemId, font)) {
            newLoadingFonts.splice(index, 1);
          }
        }));
        if (loadingFonts.length !== newLoadingFonts.length) setLoadingFonts(newLoadingFonts);
        if (newLoadingFonts.length === 0) finishLoading();
      }
    }
    loadingFontsAction();
  }, [JSON.stringify(loadingFonts), loading]);

  useEffect(() => {
    handleLoadingFonts();
  }, [dsConfig, parsedTree]);

  useEffect(() => {
    const asyncUseEfect = async () => {
      setLoading(true)
      try {
        let resultEntityInformation = {} as any;
        if (type==="template") resultEntityInformation = await getTemplate(id);
        if (type==="design") {
          getDesignAuth(id);
          resultEntityInformation = await getDesign(id)
        };
        const resultParsedTreeId = resultEntityInformation.parsedTree;
        setParsedTreeId(resultParsedTreeId);
        const result = await getParsedTree(resultParsedTreeId);
        const resultUserFile = await getUserFiles(authContext?.userData.profile.email ?? '');
        if (authContext?.userData.profile.email) await getUserConfig();
      } catch(e) {
        // TODO show something on error
        console.error(e);
        setLoading(false)  
      }
    }
    asyncUseEfect();
  }, [id])

  const returnTemplateOrDesign = (entityInfo: IEntityInformation) => {
    if (parsedTree) {
      return (
        <Provider 
          parsedTree={parsedTreeForRendering ?? parsedTree} 
          entityInformation={entityInfo}
          entityAuth={designAuth}
          userPermission={userPermission}
          userFiles={userFiles?.length && userFiles[0]}
        >
          <FontsLoading
            loading={loading}
            fonts={loadingFonts}
          />
          { (!loading && loadingFonts?.length === 0 && parsedTreeForRendering) &&
            <>
              <AppBar newTitle={design?.name ?? template?.name ?? ``}/>
              <Keyboard>
                <ContentStructure/>
              </Keyboard>
            </>
          }
        </Provider>
      );
    }
  }



  return (
    
    <S.Root >    
      { (loading || (loadingFonts && loadingFonts.length > 0) || !parsedTreeForRendering) && 
        <LoadingPage />
      }
      { template && returnTemplateOrDesign(template) }
      { design && returnTemplateOrDesign(design) }
    </S.Root>    
  );
}

export default Editor;